import { Search, Filter, MoreVertical } from "lucide-react"
import type { StudentData } from "@/lib/class-students-data"

interface StudentsTableProps {
  students: StudentData[]
}

export function StudentsTable({ students }: StudentsTableProps) {
  const getPerformanceBadge = (level: StudentData["performanceLevel"]) => {
    const badges = {
      "Top Performer": {
        color: "bg-green-100 text-green-700 border-green-200",
        icon: "",
      },
      Good: {
        color: "bg-teal-50 text-teal-700 border-teal-200",
        icon: "",
      },
      Average: {
        color: "bg-blue-50 text-blue-700 border-blue-200",
        icon: "",
      },
      "Needs Support": {
        color: "bg-yellow-50 text-yellow-700 border-yellow-200",
        icon: "",
      },
    }

    return badges[level]
  }

  const getAttendanceColor = (attendance: number) => {
    if (attendance >= 90) return "bg-green-500"
    if (attendance >= 80) return "bg-blue-500"
    if (attendance >= 70) return "bg-yellow-500"
    return "bg-red-500"
  }

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden mb-8">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-800">Enrolled Students</h3>
        <div className="flex items-center gap-2">
          <button className="p-2 text-slate-400 hover:text-teal-600 transition-colors">
            <Search className="w-5 h-5" />
          </button>
          <button className="p-2 text-slate-400 hover:text-teal-600 transition-colors">
            <Filter className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-500 w-1/12">ID</th>
              <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-500 w-1/3">
                Student Name
              </th>
              {/* <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-500 w-1/4">Attendance</th> */}
              <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-500 w-1/4">Performance</th>
              <th className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-slate-500 w-1/12 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {students.map((student) => {
              const badge = getPerformanceBadge(student.performanceLevel)
              return (
                <tr key={student.id} className="group hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6 text-slate-500 text-sm font-mono">{student.studentId}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-full ${student.avatarColor} flex items-center justify-center font-bold text-sm`}
                      >
                        {student.initials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{student.name}</p>
                        <p className="text-xs text-slate-400">Age: {student.age}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <div className="w-full bg-slate-100 rounded-full h-2 max-w-[100px]">
                        <div
                          className={`${getAttendanceColor(student.attendance)} h-2 rounded-full`}
                          style={{ width: `${student.attendance}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium text-slate-600">{student.attendance}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${badge.color} text-xs font-bold border`}
                    >
                      <span className="material-symbols-outlined text-[16px]">{badge.icon}</span>
                      {student.performanceLevel}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button className="text-slate-400 hover:text-teal-600 p-2 hover:bg-teal-600/10 rounded-lg transition-all">
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-center">
        <button className="text-sm text-teal-600 font-semibold hover:text-teal-700 transition-colors flex items-center gap-2">
          View All Students
        </button>
      </div> */}
    </div>
  )
}
